import { useEffect, useMemo, useState } from 'react'
import { Button, Table, Typography } from 'antd'
import _ from 'lodash'
import moment from 'moment'

import { Layout } from 'admin/lib/Layouts'
import { Queries, useApi } from 'admin/lib/Api'
import { ResponsiveWrapper } from 'admin/lib/Common/StyledElements'
import { massUpdatableFields } from 'admin/lib/GroupUpdateHelpers/massUpdateableFields'
import { ScreenSpin } from 'admin/lib/ScreenSpin'

import { GroupUpdateLogEntryDetails } from './GroupUpdateLogEntryDetails'

export function GroupUpdateLog() {
  const [reloadToggle, setReloadToggle] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showDetailsModal, setShowDetailsModal] = useState(null)
  const [data, setData] = useState([])
  const api = useApi()
  const users = Queries.useQflowUsers()
  const accountMappings = Queries.useUserSelfAccountMappings()

  const accounts = useMemo(() => {
    const accs = {}
    if (accountMappings.data) {
      accountMappings.data.forEach(a => {
        const account_projects = []
        a.projects.map(p => account_projects.push(p.projectId))
        accs[a.accountId] = { name: a.name, projects: account_projects }
      })
    }
    return accs
  }, [accountMappings.data])

  const projects = useMemo(() => {
    const prjs = {}
    if (accountMappings.data) {
      accountMappings.data.map(a =>
        a.projects.map(p => (prjs[p.projectId] = p.name))
      )
    }
    return prjs
  }, [accountMappings.data])

  useEffect(() => {
    const handleLoad = async () => {
      return await api.getMassUpdateLogs()
    }
    // Keep this console.info to force reload on change
    console.info(reloadToggle)
    setIsLoading(true)
    handleLoad()
      .then(value => {
        setData(value[0])
        setIsLoading(false)
      })
      .catch(() => {
        setData([])
      })
  }, [api, reloadToggle])

  const onRevert = () => {
    setReloadToggle(!reloadToggle)
  }

  const formatTableData = () => {
    let formattedData = []
    if (data && data.length > 0) {
      formattedData = data.map(item => ({
        key: item.logId,
        userId: item.userId,
        updatedAt: item.updatedAt,
        status: item.status,
        fieldName: item.fieldName,
        newValue: item.newValue,
        fieldsUpdated: item.fieldsUpdated,
        createdAt: item.createdAt
      }))
    }

    return formattedData
  }

  const handleDetails = (logId, fieldName) => {
    let moduleName = 'Delivery'
    if (fieldName in massUpdatableFields.Waste) {
      moduleName = 'Waste'
    }
    setShowDetailsModal({
      logId: logId,
      fieldName: fieldName,
      formattedFieldName: massUpdatableFields[moduleName][fieldName],
      moduleName: moduleName
    })
  }

  const handleModalClose = () => {
    setShowDetailsModal()
  }

  const userFilters = _.uniq(data?.map(a => a.userId))
    .map(userId => {
      return {
        text: users.data
          ? users.data.find(obj => obj.userId === userId)
            ? users.data.find(obj => obj.userId === userId).displayName
            : 'Unknown user'
          : userId,
        value: userId
      }
    })
    .sort((a, b) => a.text.localeCompare(b.text))

  const statusFilters = _.uniq(data?.map(a => a.status))
    .map(status => {
      return { text: status, value: status }
    })
    .sort((a, b) => a.text.localeCompare(b.text))

  const fieldNameFilters = _.uniq(data?.map(a => a.fieldName))
    .map(fieldName => {
      for (const [, value] of Object.entries(massUpdatableFields)) {
        if (value[fieldName]) {
          return { text: value[fieldName], value: fieldName }
        }
      }
      return { text: fieldName, value: fieldName }
    })
    .sort((a, b) => a.text.localeCompare(b.text))

  const columns = [
    {
      title: users && users.data ? 'User' : 'User Id',
      dataIndex: 'userId',
      filters: userFilters,
      filterMode: 'menu',
      onFilter: (value, record) => record.userId.indexOf(value) === 0,
      render: userId => {
        if (users && users.data) {
          return users.data.find(obj => obj.userId === userId)
            ? users.data.find(obj => obj.userId === userId).displayName
            : 'Unknown user'
        }
        return userId
      }
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      width: '148px',
      sorter: (a, b) =>
        moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
      render: createdAt => {
        return (
          <Typography.Text>
            {moment(createdAt).local().format('DD/MM/YYYY HH:mm')}
          </Typography.Text>
        )
      }
    },
    {
      title: 'Updated at',
      dataIndex: 'updatedAt',
      width: '148px',
      sorter: (a, b) =>
        moment(a.updatedAt).valueOf() - moment(b.updatedAt).valueOf(),
      defaultSortOrder: 'descend',
      render: updatedAt => {
        return (
          <Typography.Text>
            {moment(updatedAt).local().format('DD/MM/YYYY HH:mm')}
          </Typography.Text>
        )
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '138px',
      filters: statusFilters,
      filterMode: 'menu',
      onFilter: (value, record) => record.status.indexOf(value) === 0,
      render: status =>
        status === 'PartialReversal' ? 'Partial Reversal' : status
    },
    {
      title: 'Field name',
      dataIndex: 'fieldName',
      filters: fieldNameFilters,
      filterMode: 'menu',
      onFilter: (value, record) => record.fieldName.indexOf(value) === 0,
      render: fieldName => {
        for (const [, value] of Object.entries(massUpdatableFields)) {
          if (value[fieldName]) {
            return value[fieldName]
          }
        }
        return fieldName
      }
    },
    {
      title: 'New value',
      dataIndex: 'newValue'
    },
    {
      title: 'Fields updated',
      dataIndex: 'fieldsUpdated',
      width: '138px',
      align: 'center'
    },
    {
      title: '',
      width: '96px',
      render: (value, record) => {
        return (
          <Button
            type="secondary"
            onClick={() => handleDetails(record.key, record.fieldName)}
          >
            Details
          </Button>
        )
      }
    }
  ]

  return (
    <Layout>
      <div>
        <ResponsiveWrapper style={{ margin: '1.5rem auto' }}>
          <h1>Qupid Log</h1>
          {isLoading && <ScreenSpin id="userLoadingSpinner" />}
          {!isLoading && (
            <Table
              columns={columns}
              dataSource={formatTableData()}
              pagination={{ pageSize: 50 }}
              scroll={{ y: 'calc(100vh - 280px)' }}
              sticky
            />
          )}
        </ResponsiveWrapper>
      </div>
      {showDetailsModal && (
        <GroupUpdateLogEntryDetails
          logId={showDetailsModal.logId}
          fieldName={showDetailsModal.fieldName}
          formattedFieldName={showDetailsModal.formattedFieldName}
          moduleName={showDetailsModal.moduleName}
          handleModalClose={handleModalClose}
          accounts={accounts}
          projects={projects}
          onRevert={onRevert}
        />
      )}
    </Layout>
  )
}
